import createCache from '@emotion/cache';

import { EMOTION_CACHE_KEY } from './emotion-cache-key.mjs';
import { islandEmotionStylisPlugins } from '../../build/webpack/island-emotion-stylis-plugins.js';
import { hydrateIslandEmotionCacheFromDocument } from './hydrate-island-emotion-cache-from-document.js';
import { wrapIslandEmotionSheetFlush } from './wrap-island-emotion-sheet-flush.js';

/** @type {import('@emotion/cache').EmotionCache | null} */
let sharedIslandEmotionCache = null;

/**
 * Emotion cache for React islands. Scopes MUI :root rules to [data-react-island-root] and scales
 * rp-ui runtime rem values to match webstore html root.
 * @returns {import('@emotion/cache').EmotionCache}
 */
export function createIslandEmotionCache() {
	if (typeof document !== 'undefined' && sharedIslandEmotionCache !== null) {
		return sharedIslandEmotionCache;
	}

	if (typeof document !== 'undefined') {
		const cache = createCache({
			key: EMOTION_CACHE_KEY,
			stylisPlugins: islandEmotionStylisPlugins(),
		});

		hydrateIslandEmotionCacheFromDocument(cache);
		wrapIslandEmotionSheetFlush(cache);
		sharedIslandEmotionCache = cache;

		return cache;
	}

	return createCache({
		key: EMOTION_CACHE_KEY,
		stylisPlugins: islandEmotionStylisPlugins(),
	});
}

/**
 * SSR worker cache with hoisted theme insert keys pre-marked so Emotion skips regenerating them.
 *
 * @param {readonly string[]} themeInsertedKeys
 * @returns {import('@emotion/cache').EmotionCache}
 */
export function createSsrIslandEmotionCache(themeInsertedKeys) {
	const themeInsertedKeySet = new Set(themeInsertedKeys);
	const cache = createCache({
		key: EMOTION_CACHE_KEY,
		stylisPlugins: islandEmotionStylisPlugins(),
	});

	for (const insertedKey of themeInsertedKeys) {
		cache.inserted[insertedKey] = true;
	}

	const insertThemeStyles = cache.insert.bind(cache);
	cache.insert = (selector, serialized, sheet, shouldCache) => {
		if (themeInsertedKeySet.has(serialized.name)) {
			return;
		}

		return insertThemeStyles(selector, serialized, sheet, shouldCache);
	};

	return cache;
}

/**
 * Test-only reset for modules that share the singleton client cache.
 */
export function resetSharedIslandEmotionCacheForTests() {
	sharedIslandEmotionCache = null;
}
